/** Serializable, seeded round specifications for DEV / OG / FIRST TX (+PvP) and their judging. */
import { DIFF, PLATFORMS, SIM, type Difficulty, type FeeMode, type Mode, type Pair, type Platform } from './config';
import { ACCOUNTS, memeFrom, NOISE_TEXT, SPECIAL } from './content';
import { isMajorCatalyst } from './catalyst';
import { CUPSEY_IDS, KOL_CFG, KOLS_ALL } from './kols';
import { fakeAddr, Rng } from './rng';
import type { Art, MemeSpec, RoundSpec, ScenarioEvent, TokenIdentity, TokenSpec, Tweet } from './types';

const rr = (r: Rng, [a, b]: readonly [number, number] | readonly number[]) => r.range(a as number, b as number);

export interface RoundOptions { mode: Mode; difficulty: Difficulty; pvp: boolean; seed: number; netLatencyMs?: number }

export function makeRound(o: RoundOptions): RoundSpec {
  const r = new Rng(o.seed);
  const meme = memeFrom(r.derive('meme'));
  const target: RoundSpec['target'] = {
    name: meme.name, ticker: meme.ticker,
    platform: r.weighted<Platform>([['pump', 70], ['bonk', 15], ['bags', 15]]),
    pair: r.weighted<Pair>([['SOL', 60], ['SPACEX', 20], ['TESLA', 20]]),
    feeMode: r.weighted<FeeMode>([['none', 40], ['dividends', 40], ['feesplit', 20]]),
    art: meme.art, requireArt: o.mode === 'dev' || o.pvp,
  };
  const tweetAt = Math.round(r.range(1500, 3500));
  const events: ScenarioEvent[] = [];
  // scored rounds always use an ELIGIBLE catalyst: simulated Elon/Trump + attached image
  const poster = r.pick(SPECIAL);
  const tweet: Tweet = {
    id: `tw-${o.seed}`, t: tweetAt, author: poster, text: meme.text ?? meme.name, image: meme.art, kind: 'image', target: true, meme,
  };
  const catalyst = isMajorCatalyst(tweet) ? tweet.id : undefined;
  // distractors (never unlock major runs): ordinary chatter, special-account TEXT posts, ordinary-account IMAGE memes
  const rn = r.derive('noise');
  for (let i = 0; i < 6; i++) {
    const t = Math.round(rn.range(0, 14000));
    events.push({ t, kind: 'tweet', tweet: { id: `n-${o.seed}-${i}`, t, author: rn.pick(ACCOUNTS), text: rn.pick(NOISE_TEXT), kind: rn.chance(0.2) ? 'reply' : 'text' } });
  }
  { const t = Math.round(rn.range(0, tweetAt + 6000)); events.push({ t, kind: 'tweet', tweet: { id: `st-${o.seed}`, t, author: rn.pick(SPECIAL), text: rn.pick(['big things coming', 'working late again', 'interesting', 'the future is going to be wild']), kind: 'text' } }); }
  {
    const om = memeFrom(rn.derive('ordinary')); const t = Math.round(rn.range(500, 9000)); const id = `oi-${o.seed}`;
    events.push({ t, kind: 'tweet', tweet: { id, t, author: rn.pick(ACCOUNTS), text: om.text ?? om.name, image: om.art, kind: 'image', meme: om } });
    // an ordinary image post gets a background response only: a couple of bounded coins, no catalyst link
    for (let i = 0; i < rn.int(1, 2); i++) {
      const rt = rn.derive('oc' + i);
      events.push({ t: t + Math.round(rt.range(800, 4000)), kind: 'launch', token: { id: `r${o.seed.toString(36)}-o${i}`, name: om.name, ticker: om.ticker, platform: 'pump', pair: 'SOL', feeMode: 'none', art: om.art, address: fakeAddr(rt, 'pump'), devAddress: fakeAddr(rt), devLaunches: rt.int(5, 80), devBuySol: rt.range(0.3, 2), role: 'noise', fate: rt.weighted([['weak', 50], ['grind', 30], ['dump', 20]]) } });
    }
  }
  events.push({ t: tweetAt, kind: 'tweet', tweet });
  const d = o.difficulty;
  let n = 0;
  const tok = (patch: Partial<TokenSpec> & Pick<TokenSpec, 'role' | 'fate'>, t: number): void => {
    const rt = r.derive(`tok${n}`);
    const spec: TokenSpec = {
      id: `r${o.seed.toString(36)}-${n++}`, name: target.name, ticker: target.ticker, platform: target.platform, pair: target.pair, feeMode: target.feeMode,
      art: target.art, address: fakeAddr(rt, rt.chance(0.7) ? 'pump' : ''), devAddress: fakeAddr(rt), devLaunches: rt.int(3, 120), devBuySol: rt.range(0.5, 3), ...patch,
    };
    spec.narrativeId = tweet.id; // every round coin links to the catalyst post
    if (spec.role === 'decoy' || spec.role === 'copy') spec.tier ??= 'dead';
    if (['target', 'original', 'first'].includes(spec.role)) spec.canonical = true;
    // only canonical/linked coins of an eligible post may run; decoys/copies stay in bounded regimes
    if (catalyst && ['target', 'original', 'first', 'vamp', 'bot-dev'].includes(spec.role)) spec.catalyst = catalyst;
    events.push({ t: Math.round(t), kind: 'launch', token: spec });
  };
  const decoys = (from: number, count: number, win: number, similarity: number) => {
    for (let i = 0; i < count; i++) {
      const rd = r.derive(`decoy${i}`);
      const which = rd.int(0, target.requireArt ? 4 : 3);
      const patch: Partial<TokenSpec> = {};
      // differ in exactly one property (near-identical), sometimes two on easier difficulties
      if (which === 0) patch.ticker = rd.pick(meme.altTickers);
      if (which === 1) patch.platform = rd.pick(PLATFORMS.filter((p) => p !== target.platform));
      if (which === 2) patch.pair = rd.pick((['SOL', 'SPACEX', 'TESLA'] as const).filter((p) => p !== target.pair));
      if (which === 3) patch.feeMode = rd.pick((['none', 'dividends', 'feesplit'] as const).filter((p) => p !== target.feeMode));
      if (which === 4) patch.art = rd.chance(0.5) ? { ...target.art, crop: rd.int(1, 3) } : { ...target.art, degraded: true };
      if (rd.next() > similarity) patch.name = rd.pick([meme.name + ' Coin', 'Baby ' + meme.name, meme.name.toUpperCase()]);
      tok({ role: 'decoy', fate: rd.weighted([['weak', 50], ['dump', 30], ['rug', 20]]), ...patch }, from + rd.range(0, win));
    }
  };
  const flood = DIFF.flood[d]; const nFlood = Math.round(rr(r, flood.n));
  const similarity = d === 'casual' ? 0.5 : d === 'pro' ? 0.8 : 0.95;
  const run = DIFF.sniper[d];
  let devBotLandAt: number | undefined;
  if (o.mode === 'first') {
    const first = tweetAt + rr(r, DIFF.botFast[d]);
    decoys(tweetAt + (d === 'sweat' ? r.range(150, 400) : 200), nFlood, flood.win, similarity);
    if (o.pvp) {
      tok({ role: 'first', fate: 'dump', runMul: r.range(1.5, 3), art: { ...target.art, degraded: true }, sniperWave: true }, first);
      tok({ role: 'vamp', fate: 'runner', runMul: rr(r, run.mul) / 100 * 3, sniperWave: true }, first + rr(r, DIFF.vampDelay[d]));
    } else tok({ role: 'target', fate: 'runner', runMul: rr(r, run.mul) / 100 * 3, sniperWave: true }, first);
  } else if (o.mode === 'og') {
    const ogAt = tweetAt + r.range(300, 900);
    tok({ role: 'original', fate: 'runner', runMul: rr(r, run.mul) / 100 * 3, devLaunches: r.int(0, 2), sniperWave: true }, ogAt);
    const copies = Math.max(3, Math.round(nFlood * 0.6));
    for (let i = 0; i < copies; i++) {
      const rc = r.derive('copy' + i);
      // copies are identical in name/ticker/config; they launch later from serial deployers
      tok({ role: 'copy', fate: rc.weighted([['weak', 50], ['dump', 30], ['rug', 20]]), devLaunches: rc.int(20, 300), art: rc.chance(similarity) ? target.art : { ...target.art, crop: rc.int(1, 3) } }, ogAt + rr(r, DIFF.botFast[d]) * 0.8 + rc.range(0, flood.win));
    }
    decoys(tweetAt + 200, Math.round(nFlood * 0.4), flood.win, similarity);
  } else {
    // DEV: bot deployers race. Some rounds they fumble the ticker (devLate).
    const late = DIFF.devLate[d]; const fumble = r.chance(late.p);
    const firstCorrect = tweetAt + (fumble ? rr(r, late.at) : rr(r, DIFF.botFast[d]));
    devBotLandAt = firstCorrect;
    decoys(tweetAt + 300, nFlood, flood.win, similarity);
    tok({ role: 'bot-dev', fate: 'dump', runMul: r.range(1.5, 4), art: o.pvp ? { ...target.art, degraded: true } : target.art }, firstCorrect);
    if (o.pvp || fumble) { // bot vamps after a weak coin
      const at = firstCorrect + rr(r, DIFF.vampBot[d]);
      events.push({ t: Math.round(at), kind: 'vampBot', token: { ...baseSpec(r, target, `r${o.seed.toString(36)}-vb`), role: 'vamp', fate: 'grind', runMul: 3, catalyst } });
    }
  }
  // KOL events on the main token(s)
  const mainIds = events.filter((e): e is Extract<ScenarioEvent, { kind: 'launch' }> => e.kind === 'launch' && !!e.token.catalyst && ['target', 'original', 'vamp'].includes(e.token.role));
  for (const m of mainIds) {
    events.push(...kolPlan(r.derive('kol:' + m.token.id), m.token.id, m.t, d));
  }
  events.sort((a, b) => a.t - b.t);
  return {
    v: 1, id: `round-${o.seed.toString(36)}`, seed: o.seed, mode: o.mode, difficulty: d, pvp: o.pvp, target, meme, tweetAt,
    timeoutMs: tweetAt + DIFF.timeoutMs[o.mode], events, devBotLandAt: devBotLandAt === undefined ? undefined : Math.round(devBotLandAt), netLatencyMs: o.netLatencyMs,
  };
}

/**
 * Seeded KOL plan for a catalyst coin: a main (showcase) buy, a bounded follower chain, then partial exits.
 * Gated by the catalyst link; it is a possible event (not every round, not always after the player).
 */
export function kolPlan(r: Rng, tokenId: string, launchT: number, d: Difficulty): ScenarioEvent[] {
  const ev: ScenarioEvent[] = [];
  if (r.next() < 0.15) return ev; // sometimes nobody notable shows up
  const main = r.next() < 0.7 ? r.pick(CUPSEY_IDS) : r.pick(KOLS_ALL.filter((k) => k.archetype === 'whale')).id;
  const k = KOLS_ALL.find((x) => x.id === main)!;
  const win = ({ casual: [3000, 9000], pro: [2000, 7000], sweat: [1200, 5000] } as const)[d];
  const at = launchT + r.range(win[0], win[1]);
  ev.push({ t: Math.round(at), kind: 'kol', tokenId, side: 'buy', kolId: main, sol: r.range(...k.size) });
  const followers = KOLS_ALL.filter((x) => (x.archetype === 'follower' || x.archetype === 'early' || x.archetype === 'scalper') && x.id !== main);
  const n = r.int(...KOL_CFG.catalystFollowers); let t = at;
  const picked: string[] = [];
  for (let i = 0; i < n; i++) {
    const f = r.pick(followers); if (picked.includes(f.id)) continue; picked.push(f.id);
    t += r.range(300, 2500); ev.push({ t: Math.round(t), kind: 'kol', tokenId, side: 'buy', kolId: f.id, sol: r.range(...f.size) });
  }
  ev.push({ t: Math.round(at + r.range(9000, 18000)), kind: 'kol', tokenId, side: 'sell', kolId: main, sol: 0, fraction: r.range(0.35, 0.7) });
  for (const id of picked) if (r.next() < 0.6) ev.push({ t: Math.round(t + r.range(2000, 15000)), kind: 'kol', tokenId, side: 'sell', kolId: id, sol: 0, fraction: r.range(0.5, 1) });
  return ev;
}

function baseSpec(r: Rng, t: TokenIdentity, id: string): TokenSpec {
  return { ...t, id, address: fakeAddr(r, 'pump'), devAddress: fakeAddr(r), devLaunches: r.int(10, 200), devBuySol: 1, role: 'noise', fate: 'weak' };
}

// ---------------------------------------------------------------- judging
export type Mismatch = 'ticker' | 'platform' | 'pair' | 'feeMode' | 'art';
export function identityMismatches(a: TokenIdentity, target: RoundSpec['target']): Mismatch[] {
  const m: Mismatch[] = [];
  if (a.ticker.trim().toUpperCase() !== target.ticker.toUpperCase()) m.push('ticker');
  if (a.platform !== target.platform) m.push('platform');
  if (a.pair !== target.pair) m.push('pair');
  if (a.feeMode !== target.feeMode) m.push('feeMode');
  if (target.requireArt && artKey(a.art) !== artKey(target.art)) m.push('art');
  return m;
}
/** Canonical identity of an image: same picture, same crop/variant, not degraded, not an upload. */
export function artKey(a: Art): string {
  return JSON.stringify([a.kind ?? 'lib', a.photo ?? null, a.kind === 'photo' ? null : a.concept ?? a.seed, a.kind === 'paint' || a.kind === 'text' || a.kind === 'tweet' ? a.seed : null, a.crop ?? 0, !!a.degraded, !!a.upload, a.fx?.mirror ?? false, a.fx?.hue ?? 0, a.fx?.zoom ?? 0, a.fx?.fried ?? false]);
}
export const MISMATCH_TEXT: Record<Mismatch, string> = { ticker: 'wrong ticker', platform: 'wrong launchpad', pair: 'wrong quote pair', feeMode: 'wrong fee mode', art: 'wrong / degraded image' };

/** Landing time (ms from round start) of the first bot coin whose identity fully matches the target (DEV race). */
export function firstCorrectBotLanding(spec: RoundSpec): number {
  let best = Infinity;
  for (const e of spec.events) if ((e.kind === 'launch' || e.kind === 'vampBot') && e.token.role !== 'decoy' && identityMismatches(e.token, spec.target).length === 0) best = Math.min(best, e.t);
  return best;
}

export function isCorrectRole(spec: RoundSpec, role: string, phase: 'first' | 'second' = 'first'): boolean {
  if (spec.mode === 'first') return spec.pvp ? (phase === 'first' ? role === 'first' : role === 'vamp') : role === 'target';
  if (spec.mode === 'og') return role === 'original';
  return false;
}

/** Normalise a DOM event timestamp into the performance.now() timebase. */
export function normalizeEventTs(ts: number, timeOrigin: number, now: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return now;
  if (ts > 1e12) return ts - timeOrigin; // epoch-style
  return ts;
}

export function specSize(spec: RoundSpec): number { return new TextEncoder().encode(JSON.stringify(spec)).length; }
export const LAUNCH_ROLES_VISIBLE = false;
export function sanitizeArt(a: Art): Art { return { seed: a.seed | 0, degraded: !!a.degraded }; }
export type { MemeSpec };
export const SIM_INFO = SIM;
